import React from 'react';

export interface ColumnType<T> {
  header: string;
  accessor: keyof T;
  Cell?: (value: T[keyof T], row: T) => React.ReactNode;
}

// defining function to implement custom cell coloring based on status
export const getStatusColor = (status: string): string => {
  switch (status.toUpperCase()) {
    case 'IN PROGRESS':
      return 'text-brandWarning-500';
    case 'COMPLETED':
      return 'text-brandSuccess-500';
    case 'ERROR':
      return 'text-brandError-500';
    default:
      return '#000000';
  }
};

// Define the type for the table's props
type TableProps<T> = {
  data: T[]; // Array of data to be displayed
  columns: {
    header: string; // Column header name
    accessor: keyof T; // Key of the data object to be used
    Cell?: (value: T[keyof T], row: T) => React.ReactNode; // Optional custom cell renderer
  }[];
  rowClickHandler?: (row: T) => void;
  selectedRow?: T | null;
  className?: string; // Additional custom classes for the table
  lastRowRef?: (node: HTMLTableRowElement | null) => void;
};

function Table<T>({
  data,
  columns,
  className,
  selectedRow,
  rowClickHandler,
  lastRowRef,
}: TableProps<T>) {
  return (
    <div className={`custom-scroll overflow-y-auto pr-1 ${className || ''}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className="px-4 py-2 text-left text-xs font-medium text-gray-500"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.map((row, rowIndex) => {
            const isLastElement = rowIndex === data.length - 1;
            return (
              <tr
                key={rowIndex}
                ref={isLastElement ? lastRowRef : null}
                onClick={() => rowClickHandler?.(row)}
                className={`cursor-pointer ${selectedRow === row ? 'bg-primary-100' : 'hover:bg-primary-100'}`}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`whitespace-normal break-words px-4 py-2 text-left text-xs sm:text-sm ${selectedRow === row ? 'text-gray-900' : 'text-gray-900'}`}
                  >
                    {column.Cell
                      ? column.Cell(row[column.accessor], row)
                      : (row[column.accessor] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
